import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { creatorsApi, brandsApi, authApi, billingApi, statsApi } from '@/services/api';

const AppContext = createContext(null);

export const CONTENT_TYPES = [
  { id: 'coaching', label: 'Coaching', icon: '🎯' },
  { id: 'educational', label: 'Educational', icon: '📖' },
  { id: 'fitness', label: 'Fitness', icon: '💪' },
  { id: 'lifestyle', label: 'Lifestyle', icon: '✨' },
  { id: 'entertainment', label: 'Entertainment', icon: '🎬' },
  { id: 'premium_creator', label: 'Premium Creator', icon: '⭐' },
  { id: 'personal_brand', label: 'Personal Brand', icon: '👤' },
  { id: 'other', label: 'Other', icon: '🎭' },
];

export const PLATFORMS = [
  { id: 'instagram', label: 'Instagram' },
  { id: 'tiktok', label: 'TikTok' },
  { id: 'youtube', label: 'YouTube' },
  { id: 'x', label: 'X (Twitter)' },
  { id: 'facebook', label: 'Facebook' },
];

// Helpers
const lsGet = (key, fallback) => {
  try { const v = localStorage.getItem(key); return v !== null ? JSON.parse(v) : fallback; }
  catch { return fallback; }
};
const lsSet = (key, val) => {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch {}
};

export const AppProvider = ({ children }) => {
  // Auth / User
  const [currentUser, setCurrentUser] = useState(() => lsGet('reelia_user', null));
  const [token, setToken] = useState(() => localStorage.getItem('reelia_token'));
  const [onboardingComplete, setOnboardingCompleteState] = useState(() => lsGet('reelia_onboarded', false));
  const [userType, setUserTypeState] = useState(() => lsGet('reelia_usertype', 'agency'));

  // Creators & brands (from backend)
  const [creators, setCreators] = useState([]);
  const [creatorsLoading, setCreatorsLoading] = useState(false);
  const [activeCreatorId, setActiveCreatorId] = useState(() => lsGet('reelia_creator', null));
  const [activeBrandId, setActiveBrandId] = useState(() => lsGet('reelia_brand', null));

  // Trial / billing
  const [trialActive, setTrialActive] = useState(true);
  const [trialDaysLeft, setTrialDaysLeft] = useState(7);
  const [subscription, setSubscriptionState] = useState(null);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Derived
  const activeCreator = creators.find(c => c.id === activeCreatorId) || creators[0] || null;
  const activeBrand = activeCreator?.brands?.find(b => b.id === activeBrandId)
    || activeCreator?.brands?.[0] || null;
  const allBrands = creators.flatMap(c =>
    (c.brands || []).map(b => ({ ...b, creatorId: c.id, creatorName: c.name }))
  );

  // Persist helpers
  const setOnboardingComplete = useCallback((val) => {
    lsSet('reelia_onboarded', val);
    setOnboardingCompleteState(val);
  }, []);

  const setUserType = useCallback((val) => {
    lsSet('reelia_usertype', val);
    setUserTypeState(val);
  }, []);

  const persistUser = useCallback((user) => {
    lsSet('reelia_user', user);
    setCurrentUser(user);
  }, []);

  // Save token + user together (called from login/register responses)
  const saveAuth = useCallback((res) => {
    const { token: tok, onboarding_complete, ...userFields } = res;
    if (tok) {
      localStorage.setItem('reelia_token', tok);
      setToken(tok);
    }
    if (onboarding_complete) {
      lsSet('reelia_onboarded', true);
      setOnboardingCompleteState(true);
    }
    persistUser(userFields);
  }, [persistUser]);

  const login = useCallback(async (email, password) => {
    const res = await authApi.login({ email, password });
    saveAuth(res);
    return res;
  }, [saveAuth]);

  const logout = useCallback(() => {
    localStorage.removeItem('reelia_token');
    localStorage.removeItem('reelia_user');
    localStorage.removeItem('reelia_onboarded');
    localStorage.removeItem('reelia_creator');
    localStorage.removeItem('reelia_brand');
    localStorage.removeItem('reelia_usertype');
    setToken(null);
    setCurrentUser(null);
    setOnboardingCompleteState(false);
  }, []);

  const isAuthenticated = !!token;

  const setActiveCreator = useCallback((id) => {
    lsSet('reelia_creator', id);
    setActiveCreatorId(id);
  }, []);

  const setActiveBrand = useCallback((id) => {
    lsSet('reelia_brand', id);
    setActiveBrandId(id);
  }, []);

  // Refs to read current IDs inside loadCreators without adding them to deps
  // (avoids infinite loop: loadCreators sets these IDs → deps change → loadCreators re-runs)
  const activeCreatorIdRef = useRef(activeCreatorId);
  const activeBrandIdRef = useRef(activeBrandId);
  useEffect(() => { activeCreatorIdRef.current = activeCreatorId; }, [activeCreatorId]);
  useEffect(() => { activeBrandIdRef.current = activeBrandId; }, [activeBrandId]);

  // Load creators from backend
  const loadCreators = useCallback(async () => {
    if (!onboardingComplete) return;
    setCreatorsLoading(true);
    try {
      const agencyId = currentUser?.id;
      const list = await creatorsApi.list(userType === 'agency' ? agencyId : undefined);
      setCreators(list);
      // Set defaults only if none selected — use refs to avoid re-render loop
      if (!activeCreatorIdRef.current && list.length > 0) {
        setActiveCreator(list[0].id);
        activeCreatorIdRef.current = list[0].id;
      }
      if (!activeBrandIdRef.current && list.length > 0 && list[0].brands?.length > 0) {
        setActiveBrand(list[0].brands[0].id);
        activeBrandIdRef.current = list[0].brands[0].id;
      }
    } catch (e) {
      console.warn('Failed to load creators from backend, using local state');
    } finally {
      setCreatorsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboardingComplete, currentUser?.id, userType, setActiveCreator, setActiveBrand]);

  // Load billing info
  const loadBilling = useCallback(async () => {
    if (!currentUser?.id) return;
    try {
      const billing = await billingApi.getBilling(currentUser.id);
      setTrialActive(billing.trial_active);
      setTrialDaysLeft(billing.trial_days_left);
      setSubscriptionState(billing.subscription);
    } catch {}
  }, [currentUser?.id]);

  useEffect(() => {
    if (onboardingComplete) {
      loadCreators();
      loadBilling();
    }
  }, [onboardingComplete, loadCreators, loadBilling]);

  // CRUD helpers that call backend + refresh local state
  const addCreator = useCallback(async (data) => {
    const newCreator = await creatorsApi.create({
      ...data,
      agency_id: currentUser?.id || null,
    });
    newCreator.brands = newCreator.brands || [];
    setCreators(prev => [...prev, newCreator]);
    return newCreator;
  }, [currentUser?.id]);

  const removeCreator = useCallback(async (creatorId) => {
    await creatorsApi.delete(creatorId);
    setCreators(prev => prev.filter(c => c.id !== creatorId));
  }, []);

  const addBrand = useCallback(async (creatorId, brandData) => {
    const newBrand = await brandsApi.create({ ...brandData, creator_id: creatorId });
    setCreators(prev => prev.map(c =>
      c.id === creatorId ? { ...c, brands: [...(c.brands || []), newBrand] } : c
    ));
    return newBrand;
  }, []);

  const updateBrand = useCallback(async (brandId, creatorId, data) => {
    const updated = await brandsApi.update(brandId, data);
    setCreators(prev => prev.map(c =>
      c.id === creatorId
        ? { ...c, brands: (c.brands || []).map(b => b.id === brandId ? { ...b, ...updated } : b) }
        : c
    ));
    return updated;
  }, []);

  // ── UI Preferences ──────────────────────────────────────────────────────────
  const [showStyleMatch, setShowStyleMatchState] = useState(
    () => localStorage.getItem('reelia_show_style_match') !== 'false'
  );
  const setShowStyleMatchPreference = useCallback((val) => {
    localStorage.setItem('reelia_show_style_match', String(val));
    setShowStyleMatchState(val);
  }, []);

  const removeBrand = useCallback(async (creatorId, brandId) => {
    await brandsApi.delete(brandId);
    setCreators(prev => prev.map(c =>
      c.id === creatorId ? { ...c, brands: (c.brands || []).filter(b => b.id !== brandId) } : c
    ));
  }, []);

  const setSubscription = useCallback(async (plan, userId) => {
    if (userId || currentUser?.id) {
      await billingApi.subscribe({
        user_id: userId || currentUser.id,
        plan,
        billing_cycle: 'monthly'
      });
    }
    setSubscriptionState(plan);
    setTrialActive(false);
    // Update local user
    const updated = { ...currentUser, subscription: plan };
    persistUser(updated);
  }, [currentUser, persistUser]);

  return (
    <AppContext.Provider value={{
      // Auth
      currentUser, setCurrentUser: persistUser,
      token, isAuthenticated,
      login, logout, saveAuth,
      onboardingComplete, setOnboardingComplete,
      userType, setUserType,
      // Creators/Brands
      creators, setCreators, creatorsLoading, loadCreators,
      addCreator, removeCreator, addBrand, updateBrand, removeBrand,
      showStyleMatch, setShowStyleMatchPreference,
      activeCreatorId, setActiveCreatorId: setActiveCreator,
      activeBrandId, setActiveBrandId: setActiveBrand,
      activeCreator, activeBrand, allBrands,
      // Trial/Billing
      trialActive, trialDaysLeft, subscription, setSubscription,
      // UI
      sidebarCollapsed, setSidebarCollapsed,
    }}>
      {children}
    </AppContext.Provider>
  );
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
};
